// src/pages/Team/Person.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  NOTICED: the marks this person has been given, newest first
//               and never totalled, with a button to recognise them from here.
//   2026-09-26  Every read distinguishes an error from an empty result. The
//               first version of this file used `data || []` four times, which
//               is the exact bug it was written alongside a fix for.
//   2026-09-26  New. Tyler: the owner should be able to click one of his sales
//               managers and see their view, and click somebody on the floor
//               and see what they are doing with the system.
//
// ONE PERSON, FROM ABOVE.
//
// Everything on /team/ before this was an aggregate: how the floor is doing,
// how many are waiting, who practised this week. All of it true and none of it
// answering the question an owner actually walks in with, which is about a
// PERSON. Whether Dale is stuck. Whether the manager is running his rhythm.
//
// WHAT IT SHOWS DEPENDS ON THE JOB, and the difference is the whole point.
//
//   a salesperson   where they are in the thirteen steps, what is signed off,
//                   what is waiting on their manager, and whether they have
//                   practised. The question is "are they progressing".
//   a manager       their floor as faces, and whether the four parts of the
//                   rhythm happened this week. The question is "are they
//                   coaching", which is a completely different question and
//                   was previously unaskable about a named person.
//
// PERMISSION IS THE DATABASE'S, NOT THIS SCREEN'S.
//
// Every read here is an ordinary client query under RLS. An owner sees their
// own store because `users` is scoped by dealership, and a manager sees their
// floor for the same reason. This component does not check anything, it just
// renders what came back, and an empty result renders as "not found" rather
// than as an error. Putting a role check in here would be a SECOND copy of a
// rule that must never be wrong, which is the argument 0008 already makes
// about sign off.
//
// WHAT IT DELIBERATELY DOES NOT SHOW: coaching transcripts. The charter says
// performance yes, transcripts never, and an owner reading what somebody asked
// the coach in private is the fastest way to make everybody stop asking.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, Link as RouterLink, Navigate } from 'react-router-dom'
import {
  Box, HStack, VStack, Text, Spinner, Alert, AlertIcon, Button, Wrap, WrapItem, Divider,
} from '@chakra-ui/react'
import { ChevronLeft, Check, Clock } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { displayNameFor, firstNameFor } from '../../lib/names.js'
import { roleLabel, canSeeTeamProgress } from '../../lib/roles'
import { STEPS } from '../../content/steps'
import { dayKey, addDays } from '../../lib/localDate'
import PersonAvatar from './PersonAvatar.jsx'
import PersonMarks from './PersonMarks.jsx'

const WEEK = 7

function Stat({ label, value, tone }) {
  return (
    <Box minW="96px">
      <Text textStyle="eyebrow" color="inkDim" mb={1}>{label}</Text>
      <Text fontSize="28px" fontWeight={600} lineHeight={1} color={tone || 'ink'} fontVariantNumeric="tabular-nums">
        {value}
      </Text>
    </Box>
  )
}

// The thirteen steps as one strip. A salesperson's whole position in the method
// is a single glance, which is what an owner wants and what a list of thirteen
// rows is not.
function StepStrip({ progress }) {
  const byNumber = useMemo(() => {
    const m = {}
    for (const p of progress) if (p.steps?.number) m[p.steps.number] = p.status
    return m
  }, [progress])

  return (
    <Wrap spacing={1.5}>
      {STEPS.map((s) => {
        const status = byNumber[s.number]
        const bg =
          status === 'signed_off' ? 'success' :
          status === 'check_passed' ? 'accent.500' :
          status === 'in_progress' ? 'line' : 'transparent'
        const color = status === 'signed_off' || status === 'check_passed' ? 'white' : 'inkDim'
        return (
          <WrapItem key={s.number}>
            <Box
              minW="34px" h="34px" px={2} borderRadius="md"
              bg={bg} color={color}
              border="1px solid" borderColor={status ? 'transparent' : 'line'}
              display="flex" alignItems="center" justifyContent="center"
              fontSize="13px" fontWeight={600} fontVariantNumeric="tabular-nums"
              title={`Step ${s.number}. ${s.title}. ${status ? status.replace('_', ' ') : 'not started'}`}
            >
              {s.number}
            </Box>
          </WrapItem>
        )
      })}
    </Wrap>
  )
}

export default function Person() {
  const { personId } = useParams()
  const { user, profile } = useAuth()
  const [state, setState] = useState({ loading: true, error: null })
  const [person, setPerson] = useState(null)
  // null everywhere means NOT KNOWN. An empty array means read successfully
  // and there is nothing in it. Those are different answers and the screen
  // says different things for each.
  const [progress, setProgress] = useState(null)
  const [waiting, setWaiting] = useState(null)
  const [drills, setDrills] = useState(null)
  const [floor, setFloor] = useState(null)
  const [rhythm, setRhythm] = useState(null)

  const load = useCallback(async () => {
    if (!personId) return
    setState({ loading: true, error: null })

    // RLS decides whether this returns a row. No role check here on purpose.
    const { data: p, error } = await supabase
      .from('users')
      .select('id, display_name, first_name, last_name, initials, username, role, dealership_id')
      .eq('id', personId)
      .maybeSingle()

    if (error || !p) {
      setState({ loading: false, error: error?.message || null })
      setPerson(null)
      return
    }
    setPerson(p)

    const since = addDays(dayKey(new Date()), -WEEK)

    const [prog, signoffs, acts] = await Promise.all([
      supabase.from('step_progress').select('status, steps(number)').eq('user_id', personId),
      supabase.from('manager_signoffs').select('step_id, status, created_at').eq('user_id', personId).eq('status', 'requested'),
      supabase.from('activity_events').select('kind, created_at').eq('user_id', personId).gte('created_at', since),
    ])
    // AN ERROR IS NOT AN EMPTY RESULT, and writing `data || []` without
    // checking is how FloorView told every manager that nobody had passed
    // anything for weeks. That bug was fixed in the same commit that created
    // this file and then reproduced here four lines later, which is a decent
    // argument that the pattern is the problem rather than the programmer.
    //
    // Null means we do not know. The render shows nothing at all for a
    // section we could not read, rather than a confident zero.
    setProgress(prog.error ? null : prog.data || [])
    setWaiting(signoffs.error ? null : signoffs.data || [])
    setDrills(acts.error ? null : (acts.data || []).filter((a) => a.kind === 'practice_drill').length)

    // A manager gets their floor and their rhythm instead of a step strip,
    // because "how far through the steps is the manager" is not the question
    // anybody is asking about a manager.
    if (p.role === 'manager' || p.role === 'owner') {
      const [team, mornings, ones, audits, weeks] = await Promise.all([
        supabase.from('users')
          .select('id, display_name, first_name, last_name, initials, username, role')
          .eq('dealership_id', p.dealership_id).eq('role', 'employee'),
        supabase.from('morning_meetings').select('id').eq('led_by', personId).gte('meeting_on', since),
        supabase.from('one_on_ones').select('id').eq('manager_id', personId).gte('held_on', since),
        supabase.from('manager_audit').select('id').eq('user_id', personId).gte('on_date', since),
        supabase.from('manager_week').select('id').eq('user_id', personId).gte('week_start', addDays(since, -7)),
      ])
      setFloor(team.error ? null : team.data || [])
      // The rhythm is four counts and a zero in any of them is a real,
      // meaningful answer: it means the manager did not do that thing this
      // week. So a failed read must not render as zero here under any
      // circumstances, and one failure blanks the whole block rather than
      // showing three true numbers beside one invented one.
      const rhythmFailed = mornings.error || ones.error || audits.error || weeks.error
      setRhythm(
        rhythmFailed
          ? null
          : {
              mornings: (mornings.data || []).length,
              ones: (ones.data || []).length,
              audits: (audits.data || []).length,
              weeks: (weeks.data || []).length,
            }
      )
    }

    setState({ loading: false, error: null })
  }, [personId])

  useEffect(() => { load() }, [load])

  // Only the people who can see team progress have any business here. The
  // database would refuse the reads anyway, so this is about not showing a
  // salesperson a broken-looking empty screen.
  if (profile && !canSeeTeamProgress(profile.role)) return <Navigate to="/dashboard/" replace />
  if (state.loading) return <Spinner size="sm" />

  if (!person) {
    return (
      <VStack align="stretch" spacing={6} maxW="sheet">
        <Button as={RouterLink} to="/team/" variant="ghost" size="sm" leftIcon={<ChevronLeft size={15} />} alignSelf="flex-start">
          Back to the team
        </Button>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          That person is not on your team, or they are not in the system yet.
        </Alert>
      </VStack>
    )
  }

  const name = displayNameFor(person)
  const first = firstNameFor(person) || name
  const isFloorLead = person.role === 'manager' || person.role === 'owner'
  // `progress` is null when the read failed, so this is null rather than 0.
  // A zero here would say "nothing signed off", which is a claim, and we do
  // not have one.
  const signedOff = progress ? progress.filter((p) => p.status === 'signed_off').length : null
  const waitingCount = waiting ? waiting.length : null

  return (
    <VStack align="stretch" spacing={9} maxW="sheet">
      <Button as={RouterLink} to="/team/" variant="ghost" size="sm" leftIcon={<ChevronLeft size={15} />} alignSelf="flex-start">
        Back to the team
      </Button>

      <HStack spacing={5} align="center">
        <PersonAvatar person={person} size={64} state={waitingCount > 0 ? 'waiting' : 'none'} />
        <Box minW={0}>
          <Text textStyle="pageEyebrow" mb={1}>{roleLabel(person.role)}</Text>
          <Text textStyle="pageTitle">{name}</Text>
        </Box>
      </HStack>

      {state.error && (
        <Alert status="error" borderRadius="md"><AlertIcon />{state.error}</Alert>
      )}

      {isFloorLead ? (
        <>
          <Box>
            <Text textStyle="eyebrow" color="inkDim" mb={3}>THIS WEEK</Text>
            <Text fontSize="lg" color="inkMuted" maxW="measure" lineHeight={1.55} mb={6}>
              {!rhythm
                ? `Could not read ${first}'s week.`
                : (rhythm.mornings + rhythm.ones + rhythm.audits + rhythm.weeks) === 0
                  ? `Nothing recorded from ${first} in the last seven days. That is a fact about the record rather than about the week: three of the six daily disciplines happen in a CRM and never reach this screen.`
                  : `What ${first} has written down in the last seven days.`}
            </Text>
            {/* The numbers only appear when they are known. A zero in any of
                these means the manager did not do that thing, which is a real
                and serious statement to make about somebody, and it must never
                be what a failed read looks like. */}
            {rhythm && (
              <HStack spacing={10} flexWrap="wrap" rowGap={5}>
                <Stat label="MORNING MEETINGS" value={rhythm.mornings} />
                <Stat label="ONE ON ONES" value={rhythm.ones} />
                <Stat label="DAILY AUDITS" value={rhythm.audits} />
                <Stat label="WEEKLY RHYTHM" value={rhythm.weeks} />
              </HStack>
            )}
          </Box>

          <Divider borderColor="line" />

          <Box>
            <Text textStyle="eyebrow" color="inkDim" mb={1}>THEIR FLOOR</Text>
            <Text fontSize="lg" color="inkMuted" maxW="measure" lineHeight={1.55} mb={5}>
              {!floor
                ? 'Could not read this floor.'
                : floor.length === 0
                  ? 'Nobody is on this floor yet.'
                  : `${floor.length} ${floor.length === 1 ? 'person' : 'people'}. Tap anybody to see where they are.`}
            </Text>
            <Wrap spacing={1}>
              {(floor || []).map((m) => (
                <WrapItem key={m.id}>
                  <PersonAvatar person={m} to={`/team/${m.id}/`} />
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        </>
      ) : (
        <>
          <HStack spacing={10} flexWrap="wrap" rowGap={5}>
            {/* An em dash would be the obvious glyph for "not known" and the
                house rules ban them. "Not read" says it in words, which also
                reads out loud correctly. */}
            <Stat label="SIGNED OFF" value={signedOff === null ? 'Not read' : `${signedOff} of 13`} />
            <Stat
              label="WAITING ON YOU"
              value={waitingCount === null ? 'Not read' : waitingCount}
              tone={waitingCount > 0 ? 'accent.500' : 'ink'}
            />
            <Stat label="DRILLS THIS WEEK" value={drills === null ? 'Not read' : drills} />
          </HStack>

          {/* Hidden entirely when the read failed. A strip of thirteen empty
              boxes is not a neutral placeholder: it says this person has not
              started anything, which is the most damaging thing this page can
              say about somebody and the last thing it should say by accident. */}
          <Box>
            <Text textStyle="eyebrow" color="inkDim" mb={3}>THE THIRTEEN STEPS</Text>
            {progress ? (
              <StepStrip progress={progress} />
            ) : (
              <Text fontSize="sm" color="inkDim">Could not read this.</Text>
            )}
            {progress && (
            <HStack spacing={5} mt={4} flexWrap="wrap" rowGap={2}>
              <HStack spacing={2}>
                <Box boxSize="10px" borderRadius="sm" bg="success" />
                <Text fontSize="sm" color="inkDim">Signed off</Text>
              </HStack>
              <HStack spacing={2}>
                <Box boxSize="10px" borderRadius="sm" bg="accent.500" />
                <Text fontSize="sm" color="inkDim">Passed the check, waiting on a signature</Text>
              </HStack>
              <HStack spacing={2}>
                <Box boxSize="10px" borderRadius="sm" bg="line" />
                <Text fontSize="sm" color="inkDim">Working through it</Text>
              </HStack>
            </HStack>
            )}
          </Box>

          {waitingCount > 0 && (
            <Box bg="surface" borderRadius="card" p={5}>
              <HStack spacing={3} align="flex-start">
                <Box color="accent.500" pt="2px"><Clock size={17} /></Box>
                <Box>
                  <Text fontWeight={600} mb={1}>
                    {first} has been waiting since{' '}
                    {new Date(waiting[0].created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}.
                  </Text>
                  <Text fontSize="sm" color="inkMuted" maxW="measure" lineHeight={1.55}>
                    They passed the check and cannot start the next step until somebody watches them do
                    this one and approves it. This is the only place in the product where a person is
                    stopped by somebody else rather than by the work.
                  </Text>
                  <Button as={RouterLink} to="/team/" size="sm" mt={3}>
                    Go to the sign off queue
                  </Button>
                </Box>
              </HStack>
            </Box>
          )}

          {signedOff === 0 && waitingCount === 0 && drills === 0 && (
            <HStack spacing={3} color="inkDim">
              <Check size={16} />
              <Text fontSize="sm">
                {first} has not started yet. Nothing has gone wrong, there is just nothing to show.
              </Text>
            </HStack>
          )}
        </>
      )}

      {/* A manager or owner can recognise the person on this page. RLS decides
          whether the insert is allowed, and a refusal shows in the sheet
          rather than failing quietly. */}
      <PersonMarks
        person={person}
        viewerId={user?.id}
        dealershipId={profile?.dealership_id || null}
        canGive={profile?.role === 'manager' || profile?.role === 'owner'}
      />
    </VStack>
  )
}
